import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeRemoteServiceTaxationCodeAdmin } from "../../../usecases/service-taxation-code/remote-service-taxation-code-admin-factory";
import { makeRemoteNationalService } from "../../../usecases/national-service/remote-national-service-factory";
import { makeRemoteComplementaryLaw } from "../../../usecases/complementary-law/remote-complementary-law-factory";
import {
  CityRuleCountModel,
  TaxationLayout,
  TaxationRuleCreateModel,
  TaxationRuleModel,
  TaxationRuleUpdateModel,
} from "../../../../../domain/models/service-taxation-code";
import { NationalServiceModel } from "../../../../../domain/models/national-service";
import { ComplementaryLawModel } from "../../../../../data/usecases/complementary-law/remote-complementary-law";
import TaxationCodeAdminPage from "../../../../../presentation/pages/admin/taxation-code";
import toastUtils from "../../../../../utils/ToastUtils";

const MakeTaxationCodeAdmin: React.FC = () => {
  const adminService = useMemo(() => makeRemoteServiceTaxationCodeAdmin(), []);
  const nsService = useMemo(() => makeRemoteNationalService(), []);
  const lcService = useMemo(() => makeRemoteComplementaryLaw(), []);

  // City search state
  const [cities, setCities] = useState<CityRuleCountModel[]>([]);
  const [citySearch, setCitySearch] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const [cityTotalPages, setCityTotalPages] = useState(0);

  // Selected city + rules
  const [selectedCity, setSelectedCity] = useState<CityRuleCountModel | null>(null);
  const [rules, setRules] = useState<TaxationRuleModel[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [activeLayout, setActiveLayout] = useState<TaxationLayout>("NACIONAL");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TaxationRuleModel | null>(null);

  // Service picker options
  const [nationalServices, setNationalServices] = useState<NationalServiceModel[]>([]);
  const [complementaryLaws, setComplementaryLaws] = useState<ComplementaryLawModel[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // --- City search ---
  const handleSearchCities = useCallback(async (search: string, page = 0) => {
    try {
      setCityLoading(true);
      // Remove hífen para permitir CEP formatado (ex: 45345-000 → 45345000)
      const sanitized = search.replace(/-/g, "");
      const response = await adminService.searchCities(sanitized, page, 20);
      setCities(response.content);
      setCityTotalPages(response.totalPages);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao buscar cidades." });
    } finally {
      setCityLoading(false);
    }
  }, [adminService]);

  useEffect(() => {
    handleSearchCities(citySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCitySearchChange = useCallback((search: string) => {
    setCitySearch(search);
    handleSearchCities(search, 0);
  }, [handleSearchCities]);

  const onCityPageChange = useCallback((page: number) => {
    handleSearchCities(citySearch, page);
  }, [handleSearchCities, citySearch]);

  // --- Select city: load rules ---
  const handleSelectCity = useCallback((city: CityRuleCountModel) => {
    setSelectedCity(city);
    setActiveLayout("NACIONAL");
  }, []);

  const handleBackToCities = useCallback(() => {
    setSelectedCity(null);
    setRules([]);
  }, []);

  const loadRules = useCallback(async (ibge: string, layout: TaxationLayout) => {
    try {
      setRulesLoading(true);
      const response = await adminService.listCityRules(ibge, layout);
      setRules(response);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao carregar regras." });
    } finally {
      setRulesLoading(false);
    }
  }, [adminService]);

  useEffect(() => {
    if (selectedCity) {
      loadRules(selectedCity.ibge, activeLayout);
    }
  }, [selectedCity, activeLayout, loadRules]);

  const handleLayoutChange = useCallback((layout: TaxationLayout) => {
    setActiveLayout(layout);
  }, []);

  // --- Picker (busca de serviço no modal) ---
  const handleSearchService = useCallback(async (query: string, layout: TaxationLayout) => {
    try {
      setPickerLoading(true);
      if (layout === "NACIONAL") {
        const results = await nsService.search(query);
        setNationalServices(results);
      } else {
        const results = await lcService.search(query);
        setComplementaryLaws(results);
      }
    } catch {
      // silently fail
    } finally {
      setPickerLoading(false);
    }
  }, [nsService, lcService]);

  // Load services when opening modal
  useEffect(() => {
    if (modalOpen) {
      handleSearchService("", activeLayout);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // --- CRUD ---
  const handleOpenCreate = useCallback(() => {
    setEditingRule(null);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((rule: TaxationRuleModel) => {
    setEditingRule(rule);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingRule(null);
  }, []);

  const handleSave = useCallback(async (data: TaxationRuleCreateModel | TaxationRuleUpdateModel) => {
    if (!selectedCity) return;
    try {
      if (editingRule) {
        await adminService.updateRule(editingRule.id, data as TaxationRuleUpdateModel);
        toastUtils.showMessage({ type: "success", message: "Regra atualizada!" });
      } else {
        await adminService.createCityRule(selectedCity.ibge, data as TaxationRuleCreateModel);
        toastUtils.showMessage({ type: "success", message: "Regra criada!" });
      }
      handleCloseModal();
      loadRules(selectedCity.ibge, activeLayout);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao salvar regra." });
    }
  }, [selectedCity, editingRule, adminService, handleCloseModal, loadRules, activeLayout]);

  const handleDelete = useCallback(async (id: number) => {
    if (!selectedCity) return;
    try {
      await adminService.deleteRule(id);
      toastUtils.showMessage({ type: "success", message: "Regra excluída!" });
      loadRules(selectedCity.ibge, activeLayout);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao excluir regra." });
    }
  }, [selectedCity, adminService, loadRules, activeLayout]);

  return (
    <TaxationCodeAdminPage
      // city search
      cities={cities}
      citySearch={citySearch}
      cityLoading={cityLoading}
      cityTotalPages={cityTotalPages}
      onCitySearchChange={onCitySearchChange}
      onCityPageChange={onCityPageChange}
      onSelectCity={handleSelectCity}
      // city rules
      selectedCity={selectedCity}
      rules={rules}
      rulesLoading={rulesLoading}
      activeLayout={activeLayout}
      onLayoutChange={handleLayoutChange}
      onBackToCities={handleBackToCities}
      // CRUD
      onOpenCreate={handleOpenCreate}
      onOpenEdit={handleOpenEdit}
      onDelete={handleDelete}
      // modal
      modalOpen={modalOpen}
      editingRule={editingRule}
      onCloseModal={handleCloseModal}
      onSave={handleSave}
      // picker
      nationalServices={nationalServices}
      complementaryLaws={complementaryLaws}
      pickerLoading={pickerLoading}
      onSearchService={handleSearchService}
    />
  );
};

export default MakeTaxationCodeAdmin;
